import { CloseOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter, FilterField } from "@/components/BlotterFilterBar";
import { useAddGroupUser, useGroupUsers, useRemoveGroupUser } from "@/hooks/useGroups";
import { useRoles } from "@/hooks/useRoles";
import { useUsers } from "@/hooks/useUsers";
import type { GroupUser } from "@/interfaces/group";

const STATUS_OPTIONS: FilterField["options"] = [
  { value: "Enabled", label: "Enabled" },
  { value: "Invited", label: "Invited" },
];

const statusOf = (m: GroupUser): string => (m.verified ? "Enabled" : "Invited");

/**
 * A group's Users tab. A user belongs to a group via a group_membership row, so the same user
 * can be in several groups. Adding assigns a group-level role; removing deletes only this group's
 * membership. The member list is a Blotter (search / sort / pagination) over the group's members,
 * which come as one flat list — so the Blotter data hook paginates client-side.
 */
export default function GroupUsersPanel({
  group,
  organizationId,
}: {
  group: { id: number; name: string };
  organizationId: number;
}) {
  const groupId = group.id;
  const { data: members = [], isLoading } = useGroupUsers(groupId);
  const { data: orgUsers } = useUsers({ organizationId, limit: 100 });
  const { roles } = useRoles();
  const addUser = useAddGroupUser(organizationId);
  const removeUser = useRemoveGroupUser(organizationId);

  // Only group-level roles are assignable in a group (org/GFG roles live elsewhere).
  const groupRoles = roles.filter((r) => r.level === "group");

  const [pickUser, setPickUser] = useState<{ id: number; label: string } | null>(null);
  const [pickRole, setPickRole] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPickUser(null);
    setError(null);
  }, []);
  useEffect(() => {
    const first = groupRoles[0];
    if (!pickRole && first) setPickRole(first.key);
  }, [groupRoles, pickRole]);

  const memberIds = new Set(members.map((m) => m.id));
  const options = (orgUsers?.data ?? [])
    .filter((u) => !u.archived && !memberIds.has(u.id))
    .map((u) => ({ id: u.id, label: u.name ? `${u.name} · ${u.email}` : u.email }));

  function handleAdd() {
    if (!pickUser || !pickRole) return;
    setError(null);
    addUser.mutate(
      { groupId, userId: pickUser.id, role: pickRole },
      {
        onSuccess: () => setPickUser(null),
        onError: (e) => setError(e instanceof Error ? e.message : "Failed to add user."),
      },
    );
  }
  function handleRole(userId: number, role: string) {
    setError(null);
    addUser.mutate(
      { groupId, userId, role },
      { onError: (e) => setError(e instanceof Error ? e.message : "Failed to change role.") },
    );
  }
  function handleRemove(userId: number) {
    setError(null);
    removeUser.mutate(
      { groupId, userId },
      { onError: (e) => setError(e instanceof Error ? e.message : "Failed to remove user.") },
    );
  }

  // Client-side search / sort / pagination over the flat member list (no hooks in here — it closes
  // over the members already fetched above, so it's a plain function the Blotter calls each render).
  const sortVal = (m: GroupUser, key: string) =>
    (key === "email"
      ? m.email
      : key === "role"
        ? m.role
        : key === "status"
          ? statusOf(m)
          : (m.name ?? "")
    ).toLowerCase();
  const filterMembers = (search: string, filters: AppliedFilter[]) => {
    const valuesFor = (id: string) => filters.find((f) => f.fieldId === id)?.values ?? [];
    const statuses = valuesFor("status");
    const roleFilter = valuesFor("role");
    const s = search.trim().toLowerCase();
    return members.filter((m) => {
      if (s && !(m.name ?? "").toLowerCase().includes(s) && !m.email.toLowerCase().includes(s)) {
        return false;
      }
      if (statuses.length && !statuses.includes(statusOf(m))) return false;
      if (roleFilter.length && !roleFilter.includes(m.role)) return false;
      return true;
    });
  };
  const membersData = (q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<GroupUser> => {
    let rows = filterMembers(q.search, q.filters);
    if (q.sortBy) {
      rows = [...rows].sort((a, b) => {
        const cmp = sortVal(a, q.sortBy).localeCompare(sortVal(b, q.sortBy));
        return q.sortDir === "asc" ? cmp : -cmp;
      });
    }
    const total = rows.length;
    const start = (q.page - 1) * q.limit;
    return {
      data: rows.slice(start, start + q.limit),
      total,
      totalPages: Math.max(1, Math.ceil(total / q.limit)),
      isLoading,
    };
  };

  const config: BlotterConfig<GroupUser> = {
    title: "Members",
    searchPlaceholder: "Search members",
    itemNoun: "member",
    minWidth: 520,
    pageSize: 10,
    rowKey: (m) => m.id,
    filterFields: [
      { id: "status", label: "Status", options: STATUS_OPTIONS },
      {
        id: "role",
        label: "Group Role",
        options: groupRoles.map((r) => ({ value: r.key, label: r.label })),
      },
    ],
    exportFilename: "group-members.csv",
    exportFetch: async (q) => ({ data: filterMembers(q.search, q.filters), totalPages: 1 }),
    useData: membersData,
    columns: [
      {
        key: "name",
        label: "Name",
        sortable: true,
        sortValue: (m) => m.name ?? "",
        render: (m) => <span style={{ fontWeight: 600 }}>{m.name ?? "—"}</span>,
      },
      {
        key: "email",
        label: "Email",
        sortable: true,
        sortValue: (m) => m.email,
        render: (m) => <span style={{ color: "var(--color-graphite)" }}>{m.email}</span>,
      },
      {
        key: "role",
        label: "Group Role",
        render: (m) => (
          <Select
            value={m.role}
            onChange={(e) => handleRole(m.id, e.target.value)}
            size="small"
            sx={{ minWidth: 150, fontSize: 14, bgcolor: "background.paper" }}
          >
            {groupRoles.map((r) => (
              <MenuItem key={r.key} value={r.key} sx={{ fontSize: 14 }}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        sortValue: (m) => statusOf(m),
        render: (m) => statusOf(m),
      },
      {
        key: "actions",
        label: "",
        render: (m) => (
          <IconButton
            size="small"
            title="Remove from group"
            onClick={() => handleRemove(m.id)}
            disabled={removeUser.isPending}
          >
            <CloseOutlined sx={{ fontSize: 18, color: "var(--color-good-wine)" }} />
          </IconButton>
        ),
      },
    ],
  };

  return (
    <Box>
      {/* Add member — stays above the member blotter. */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, maxWidth: 640 }}>
        <Autocomplete
          options={options}
          value={pickUser}
          onChange={(_e, v) => setPickUser(v)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderInput={(params) => <TextField {...params} label="Add user" size="small" />}
          sx={{ flex: 1 }}
          size="small"
        />
        <Select
          value={pickRole}
          onChange={(e) => setPickRole(e.target.value)}
          size="small"
          sx={{ minWidth: 150, fontSize: 14 }}
        >
          {groupRoles.map((r) => (
            <MenuItem key={r.key} value={r.key} sx={{ fontSize: 14 }}>
              {r.label}
            </MenuItem>
          ))}
        </Select>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!pickUser || !pickRole || addUser.isPending}
          sx={{ bgcolor: "var(--color-rodeo-black)" }}
        >
          Add
        </Button>
      </Box>
      {error && <Typography sx={{ fontSize: 13, color: "error.main", mb: 1 }}>{error}</Typography>}

      <Blotter config={config} />
    </Box>
  );
}
